import { EditorColorService } from '../../../_services';

// Text color paired with a theme-applied background so it stays readable
const AUTO_TEXT_COLOR = '#ffffff';

export class BackgroundColorPlugin {
  private tinymce: any;

  constructor(tinymce: any, private editorColorService: EditorColorService) {
    this.tinymce = tinymce;
  }

  create() {
    const self = this;

    this.tinymce.PluginManager.add('background-color', function (editor: any, url: string) {
      // Add toolbar button
      editor.ui.registry.addButton('background-color', {
        icon: 'fill',
        tooltip: 'Set background color',
        onAction: function () {
          self.openColorPicker(editor);
        },
      });

      // Add menu item
      editor.ui.registry.addMenuItem('background-color', {
        text: 'Background Color',
        onAction: function () {
          self.openColorPicker(editor);
        },
      });

      // Setup command
      editor.addCommand('mceBackgroundColor', function (ui: boolean, color: string) {
        self.setBackgroundColor(editor, color);
      });

      // Theme-applied background pairs a readable text color and marks the editor as auto-styled
      editor.addCommand('mceThemeBackgroundColor', function (ui: boolean, color: string) {
        self.setBackgroundColor(editor, color, AUTO_TEXT_COLOR);
      });

      // Clicks inside the editor close the shared color picker; a destroyed editor releases it
      editor.on('mousedown', function () {
        self.editorColorService.dismiss();
      });
      editor.on('remove', function () {
        self.editorColorService.release(editor);
      });

      // Initialize background color on editor init
      editor.on('init', function () {
        // Small delay to ensure editor is fully ready
        setTimeout(() => {
          self.initializeBackgroundColor(editor);
        }, 100);
      });
    });
  }

  // Opens the shared brand-swatch picker; Cancel restores the previous background on the editor UI too.
  private openColorPicker(editor: any) {
    const previous = editor.backgroundColor;
    const container = editor.getContainer();
    const button =
      container.querySelector('[aria-label="Set background color"]') ||
      container.querySelector('[title="Set background color"]') ||
      container.querySelector('button[data-mce-name="background-color"]') ||
      container.querySelector('.tox-toolbar__group button[data-mce-tooltip*="Background"]') ||
      container.querySelector('.tox-toolbar__group button[aria-label*="background"]') ||
      container;

    this.editorColorService.toggle(editor, 'background-color', button, {
      color: previous || '',
      apply: (color: string) => this.setBackgroundColor(editor, color),
      revert: () => {
        editor.backgroundColor = previous;
        this.applyToEditorUI(editor, previous || '');
      },
    });
  }

  private setBackgroundColor(editor: any, color: string, autoTextColor?: string) {
    if (color) {
      // Store background color as custom property for retrieval
      editor.backgroundColor = color;

      const body = editor.getBody();
      const container = editor.getContainer();

      // Set background color on editor body
      body.style.backgroundColor = color;

      // Auto text color lives only while the theme background is active; a manual change clears it
      let clearAutoText = false;
      if (autoTextColor) {
        editor.autoTextColor = autoTextColor;
        body.style.color = autoTextColor;
      } else if (editor.autoTextColor) {
        clearAutoText = true;
        editor.autoTextColor = null;
        body.style.color = '';
      }

      // Set background color on editor iframe and statusbar
      const editorIframe = container.querySelector('.tox-edit-area__iframe') as HTMLElement;
      if (editorIframe) {
        editorIframe.style.backgroundColor = color;
      }

      const statusbar = container.querySelector('.tox-statusbar') as HTMLElement;
      if (statusbar) {
        statusbar.style.backgroundColor = color;
      }

      // Apply background to content if needed
      const content = editor.getContent();
      if (content && content.trim()) {
        this.updateContentBackground(editor, color, autoTextColor, clearAutoText);
      }

      // Trigger change event
      editor.fire('Change');
    }
  }

  private updateContentBackground(editor: any, color: string, applyTextColor?: string, clearTextColor?: boolean) {
    // Prevent recursive calls
    if (editor._updatingBackground) {
      return;
    }

    const content = editor.getContent();
    if (content && content.trim()) {
      editor._updatingBackground = true;

      try {
        // Create a temporary DOM element to parse and modify the content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        const firstChild = tempDiv.firstElementChild;
        const textStyle = applyTextColor ? ` color: ${applyTextColor};` : '';
        if (firstChild) {
          // Update or add background-color to the root element
          const existingStyle = firstChild.getAttribute('style') || '';

          // Remove existing background-color and padding
          let updatedStyle = existingStyle.replace(/background-color:\s*[^;]+;?/gi, '').trim();
          updatedStyle = updatedStyle.replace(/padding:\s*[^;]+;?/gi, '').trim();

          // Reset the auto-applied text color before re-adding or clearing it
          if (applyTextColor || clearTextColor) {
            updatedStyle = updatedStyle.replace(/(^|[^-])color:\s*[^;]+;?/gi, '$1').trim();
          }

          // Add the new background-color and padding
          if (updatedStyle && !updatedStyle.endsWith(';')) {
            updatedStyle += ';';
          }
          updatedStyle += ` background-color: ${color}; padding: 5px;${textStyle}`;

          firstChild.setAttribute('style', updatedStyle);

          const updatedContent = tempDiv.innerHTML;
          if (updatedContent !== content) {
            editor.setContent(updatedContent, { format: 'html' });
          }
        } else if (content.trim() !== '') {
          // If no root element, wrap content in a div with background color and padding
          const wrappedContent = `<div style="background-color: ${color}; padding: 5px;${textStyle}">${content}</div>`;
          editor.setContent(wrappedContent, { format: 'html' });
        }
      } finally {
        // Always clear the flag
        setTimeout(() => {
          editor._updatingBackground = false;
        }, 10);
      }
    }
  }

  private initializeBackgroundColor(editor: any) {
    // Extract background color from content if it exists
    const content = editor.getContent();
    if (content && content.trim()) {
      const backgroundColor = this.extractBackgroundColorFromContent(content);
      if (backgroundColor) {
        editor.backgroundColor = backgroundColor;
        // Apply to editor UI only, don't modify content again
        this.applyToEditorUI(editor, backgroundColor);
      }
    }
  }

  private applyToEditorUI(editor: any, color: string) {
    const body = editor.getBody();
    const container = editor.getContainer();

    // Set background color on editor body
    body.style.backgroundColor = color;

    // Set background color on editor iframe and statusbar
    const editorIframe = container.querySelector('.tox-edit-area__iframe') as HTMLElement;
    if (editorIframe) {
      editorIframe.style.backgroundColor = color;
    }

    const statusbar = container.querySelector('.tox-statusbar') as HTMLElement;
    if (statusbar) {
      statusbar.style.backgroundColor = color;
    }
  }

  private extractBackgroundColorFromContent(content: string): string | null {
    let result: string | null = null;
    
    if (content && content.trim()) {
      // Create a temporary DOM element to parse the content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Look for background-color only in the outermost element (root div)
      const firstChild = tempDiv.firstElementChild;
      if (firstChild) {
        const style = firstChild.getAttribute('style');
        if (style) {
          const match = style.match(/background-color:\s*([^;]+)/i);
          if (match && match[1]) {
            result = match[1].trim();
          }
        }
      }
    }

    return result;
  }

  // Public method to get background color for external use
  public getBackgroundColor(editor: any): string | null {
    return editor.backgroundColor || null;
  }

  // Public method to set background color programmatically
  public setBackground(editor: any, color: string) {
    this.setBackgroundColor(editor, color);
  }
}
